import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Globe, Shield, RefreshCw, AlertTriangle, ArrowRight } from 'lucide-react';
import { predictionPeriods, regions } from '../data/aiPredictorData';
import {
  inputDark, LabelDark, SelectDark, OptionDark, CurrencySelect, btnPrimary, Spinner, SectionLabel, Flag, money, fmtDate, fmtRate, MONTHS,
} from '../components/ai/ui';
import TravelResults from '../components/ai/TravelFinder';
import MarketData from '../components/ai/MarketData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIPredictor() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('rate');

  // ---- rate outlook state
  const [currencies, setCurrencies] = useState([]);
  const [code, setCode] = useState('USD');
  const [days, setDays] = useState('30');
  const [amount, setAmount] = useState('');
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ---- trip finder state
  const [origins, setOrigins] = useState([{ code: 'YVR', city: 'Vancouver' }]);
  const [trip, setTrip] = useState({ budget: '', month: '', region: 'all', days: 7, origin: 'YVR', travelers: 1 });
  const [tripResults, setTripResults] = useState([]);
  const [tripMeta, setTripMeta] = useState(null);
  const [tripLoading, setTripLoading] = useState(false);
  const [tripSearched, setTripSearched] = useState(false);
  const setT = (k) => (e) => setTrip((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    axios.get(`${API_URL}/api/ai/currencies`).then((r) => {
      const list = r.data.currencies || [];
      setCurrencies(list);
      if (list.length && !list.find((c) => c.code === code)) setCode(list[0].code);
    }).catch(() => {});
    axios.get(`${API_URL}/api/ai/travel-origins`).then((r) => r.data.origins?.length && setOrigins(r.data.origins)).catch(() => {});
    axios.get(`${API_URL}/api/ai/countries?region=all`).then((r) => setTripResults((r.data.countries || []).slice(0, 9))).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const runPrediction = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/ai/predict-currency`, {
        currency_code: code, days: parseInt(days), amount: amount ? parseFloat(amount) : null,
      });
      setPrediction(data);
      setTimeout(() => document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (err) {
      setError(err.response?.data?.detail || 'We could not build the outlook right now. Please try again in a moment.');
    } finally { setLoading(false); }
  };

  const runTrips = async () => {
    setTripLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/ai/travel-recommendations`, {
        budget: trip.budget ? parseFloat(trip.budget) : null, region: trip.region, month: trip.month || null,
        days: parseInt(trip.days), origin: trip.origin, travelers: parseInt(trip.travelers),
      });
      setTripResults(data.recommendations || []);
      setTripMeta(data.meta || null);
      setTripSearched(true);
      setTimeout(() => document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (err) {
      console.error('Trip search failed:', err);
    } finally { setTripLoading(false); }
  };

  const signUp = () => navigate('/auth?mode=register');
  const current = currencies.find((c) => c.code === code);
  const pct = prediction ? (prediction.predicted_buy_rate / prediction.current_buy_rate - 1) * 100 : 0;
  const up = prediction?.trend === 'up';
  const down = prediction?.trend === 'down';
  const TrendIcon = up ? TrendingUp : down ? TrendingDown : Minus;

  const ChartTip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-xs">
        <p className="text-slate-500 mb-1">{new Date(label).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} · {payload[0].payload.type}</p>
        {payload.map((p, i) => <p key={i} className="font-mono text-slate-900">{p.name}: {fmtRate(p.value)}</p>)}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white" data-testid="ai-predictor-page">
      {/* ================= HERO (navy, same as homepage) ================= */}
      <section className="bg-[#001435]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-28 md:pt-20 md:pb-36">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="space-y-6 lg:pt-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
                Know the rate<br /><span className="text-blue-400">before you exchange</span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100/70 max-w-xl leading-relaxed">
                A rate outlook for every currency we sell, built on today's Bank of Canada and US Fed data.
                Plus a trip finder that shows where your Canadian dollar goes furthest.
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="trust-badge"><Shield className="w-4 h-4" /><span>FINTRAC Registered</span></div>
                <div className="trust-badge"><RefreshCw className="w-4 h-4" /><span>Market data refreshed hourly</span></div>
              </div>
              <p className="text-sm text-blue-100/50 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Outlooks are estimates, not financial advice.</p>
            </div>

            {/* Panel — styled like the homepage rate board */}
            <div className="rate-board">
              <div className="grid grid-cols-2 gap-2 mb-6">
                <button onClick={() => setTab('rate')} className={`h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition ${tab === 'rate' ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}>
                  <TrendingUp className="w-4 h-4" /> Rate Outlook
                </button>
                <button onClick={() => setTab('trip')} className={`h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition ${tab === 'trip' ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}>
                  <Globe className="w-4 h-4" /> Trip Finder
                </button>
              </div>

              {tab === 'rate' ? (
                <>
                  <h2 className="text-2xl font-bold text-white mb-1">Rate Outlook</h2>
                  <p className="text-blue-200/50 text-sm mb-5">Where a currency may be in 7 days to a year, in CAD</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 sm:col-span-1">
                      <LabelDark>Currency</LabelDark>
                      <CurrencySelect value={code} onChange={setCode} currencies={currencies} testId="currency-select" />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <LabelDark>Horizon</LabelDark>
                      <SelectDark value={days} onChange={(e) => setDays(e.target.value)} data-testid="period-select">
                        {predictionPeriods.map((p) => <OptionDark key={p.value} value={p.value}>{p.label}</OptionDark>)}
                      </SelectDark>
                    </div>
                    <div>
                      <LabelDark>Amount (CAD)</LabelDark>
                      <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Optional" className={`${inputDark} font-mono`} data-testid="amount-input" />
                    </div>
                    <div className="flex items-end">
                      <button onClick={runPrediction} disabled={loading || !currencies.length} className={`${btnPrimary} w-full`} data-testid="predict-button">
                        {loading ? <Spinner /> : null}{loading ? 'Reading the market…' : 'Get outlook'}{!loading && <ArrowRight className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {current && (
                    <div className="rate-row mt-5">
                      <div className="flex items-center gap-3"><Flag code={current.code} className="w-8 h-8" /><span className="text-white font-semibold">{current.code} today</span></div>
                      <div className="font-mono text-sm sm:text-base"><span className="text-green-400">{fmtRate(current.sell_rate)}</span><span className="text-white/30 mx-2">·</span><span className="text-red-400">{fmtRate(current.buy_rate)}</span></div>
                    </div>
                  )}
                  {error && <p className="mt-4 text-sm text-red-300 flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />{error}</p>}
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-white mb-1">Trip Finder</h2>
                  <p className="text-blue-200/50 text-sm mb-5">Ranked by where your dollar goes furthest, using today's FonderX rates</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <LabelDark>From</LabelDark>
                      <SelectDark value={trip.origin} onChange={setT('origin')}>
                        {origins.map((o) => <OptionDark key={o.code} value={o.code}>{o.city} ({o.code})</OptionDark>)}
                      </SelectDark>
                    </div>
                    <div>
                      <LabelDark>Budget (CAD)</LabelDark>
                      <input type="number" min="0" value={trip.budget} onChange={setT('budget')} placeholder="3000" className={`${inputDark} font-mono`} data-testid="budget-input" />
                    </div>
                    <div>
                      <LabelDark>Month</LabelDark>
                      <SelectDark value={trip.month} onChange={setT('month')} data-testid="month-select">
                        <OptionDark value="">Any time</OptionDark>
                        {MONTHS.map((m) => <OptionDark key={m} value={m}>{m}</OptionDark>)}
                      </SelectDark>
                    </div>
                    <div>
                      <LabelDark>Region</LabelDark>
                      <SelectDark value={trip.region} onChange={setT('region')} data-testid="region-select">
                        {regions.map((r) => <OptionDark key={r.value} value={r.value}>{r.label}</OptionDark>)}
                      </SelectDark>
                    </div>
                    <div>
                      <LabelDark>Days</LabelDark>
                      <SelectDark value={trip.days} onChange={setT('days')}>
                        {[3, 4, 5, 7, 10, 14, 21, 28].map((d) => <OptionDark key={d} value={d}>{d} days</OptionDark>)}
                      </SelectDark>
                    </div>
                    <div>
                      <LabelDark>Travellers</LabelDark>
                      <SelectDark value={trip.travelers} onChange={setT('travelers')}>
                        {[1, 2, 3, 4, 5, 6].map((n) => <OptionDark key={n} value={n}>{n}</OptionDark>)}
                      </SelectDark>
                    </div>
                    <div className="col-span-2">
                      <button onClick={runTrips} disabled={tripLoading} className={`${btnPrimary} w-full`} data-testid="search-travel-button">
                        {tripLoading ? <Spinner /> : null}{tripLoading ? 'Comparing destinations…' : 'Find trips'}{!tripLoading && <ArrowRight className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {/* curve into the white section, same as the homepage */}
        <svg className="w-full h-16 md:h-24 block -mt-1" viewBox="0 0 1440 96" preserveAspectRatio="none"><path fill="white" d="M0,96 L0,48 Q720,0 1440,48 L1440,96 Z" /></svg>
      </section>

      {/* ================= RESULTS (white) ================= */}
      <section id="results" className="bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-20">
          {tab === 'rate' && (prediction ? (
            <>
              <SectionLabel>{prediction.days}-day outlook</SectionLabel>
              <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-3"><Flag code={prediction.currency_code} className="w-9 h-9" />{prediction.currency_name}</h2>
                  <p className="text-slate-500 mt-1">1 {prediction.currency_code} in CAD at FonderX · today vs. {fmtDate(prediction.chart_data?.[prediction.chart_data.length - 1]?.date)}</p>
                </div>
                <span className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-semibold ${up ? 'bg-green-100 text-green-800' : down ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>
                  <TrendIcon className="w-4 h-4" /> {prediction.currency_code} {up ? 'strengthening' : down ? 'weakening' : 'steady'} vs CAD
                </span>
              </div>

              <div className="grid lg:grid-cols-5 gap-5">
                {/* main card */}
                <div className="card-light lg:col-span-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">We sell in {prediction.days} days</p>
                  <p className="font-mono text-4xl md:text-5xl font-semibold text-slate-900 leading-none">
                    {fmtRate(prediction.predicted_buy_rate)}
                    <span className={`text-lg ml-3 ${pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{pct >= 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(2)}%</span>
                  </p>

                  {prediction.chart_data?.length > 0 && (
                    <div className="h-56 mt-5 -ml-3">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={prediction.chart_data} margin={{ top: 10, right: 6, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="fx" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity={0.22} /><stop offset="100%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                          </defs>
                          <CartesianGrid stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => new Date(v).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} minTickGap={40} />
                          <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} domain={['auto', 'auto']} tickFormatter={(v) => fmtRate(v)} width={58} />
                          <Tooltip content={<ChartTip />} />
                          <ReferenceLine x={prediction.chart_data[0]?.date} stroke="#cbd5e1" strokeDasharray="4 4" label={{ value: 'today', fill: '#94a3b8', fontSize: 11, position: 'insideTopRight' }} />
                          <Area type="monotone" dataKey="buy_rate" name="We sell" stroke="#3b82f6" strokeWidth={2.5} fill="url(#fx)" dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                          <Area type="monotone" dataKey="sell_rate" name="We buy" stroke="#22c55e" strokeWidth={2} fill="transparent" dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                    {[
                      ['We buy today', prediction.current_sell_rate, ''],
                      [`We buy in ${prediction.days}d`, prediction.predicted_sell_rate, up ? 'text-green-600' : down ? 'text-red-600' : ''],
                      ['We sell today', prediction.current_buy_rate, ''],
                      [`We sell in ${prediction.days}d`, prediction.predicted_buy_rate, up ? 'text-green-600' : down ? 'text-red-600' : ''],
                    ].map(([l, v, cls]) => (
                      <div key={l} className="border border-slate-200 rounded-xl px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{l}</p>
                        <p className={`font-mono text-xl font-semibold text-slate-900 ${cls}`}>{fmtRate(v)}</p>
                      </div>
                    ))}
                  </div>

                  {prediction.advice ? (
                    <div className="mt-5 grid sm:grid-cols-2 gap-3">
                      {[['Buying ' + prediction.currency_code + '?', prediction.advice.buying, true], ['Selling ' + prediction.currency_code + '?', prediction.advice.selling, false]].map(([q, a, isBuy]) => {
                        const now = /now/i.test(a.action);
                        const tone = /either/i.test(a.action) ? 'bg-slate-50 border-slate-200 text-slate-800' : now ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800';
                        return (
                          <div key={q} className={`rounded-xl border p-4 ${tone}`}>
                            <p className="text-[11px] font-semibold uppercase tracking-wider opacity-70 mb-1">{q}</p>
                            <p className="text-xl font-bold mb-1.5">{a.action}</p>
                            {isBuy && prediction.amount_cad && (
                              <p className="text-sm text-slate-700 mb-1">Your {money(prediction.amount_cad)} buys <span className="font-mono font-semibold">{prediction.current_value?.toLocaleString()}</span> today vs <span className="font-mono font-semibold">{prediction.predicted_value?.toLocaleString()}</span> {prediction.currency_code} in {prediction.days} days.</p>
                            )}
                            <p className="text-sm text-slate-600">{a.reason}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mt-5 rounded-xl border p-4 bg-blue-50 border-blue-200">
                      <p className="font-bold mb-1 text-blue-800">{prediction.action}</p>
                      <p className="text-sm text-slate-600">{prediction.action_reason}</p>
                    </div>
                  )}
                </div>

                {/* why card */}
                <div className="card-light lg:col-span-2">
                  <SectionLabel>What's moving it</SectionLabel>
                  <p className="text-slate-700 leading-relaxed mb-4">{prediction.explanation}</p>
                  <div className="divide-y divide-slate-100">
                    {prediction.factors?.map((f, i) => (
                      <div key={i} className="py-3 flex gap-3 text-sm text-slate-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />{f}
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                    <span>Bank of Canada · US Fed · ECB · FonderX rates</span>
                    <span className="font-mono">Confidence {prediction.confidence}%</span>
                  </div>
                </div>
              </div>
              <MarketData data={prediction.market_data} currency={prediction.currency_code} />
            </>
          ) : (
            <div className="text-center py-10 text-slate-500 text-sm">
              Pick a currency above and tap <span className="font-semibold text-slate-700">Get outlook</span>.
            </div>
          ))}

          {tab === 'trip' && (
            <TravelResults results={tripResults} meta={tripMeta} search={trip} searched={tripSearched}
              isAuthenticated={isAuthenticated} onSignUp={signUp} />
          )}
        </div>
      </section>
    </div>
  );
}
